/**
 * Frontend caching utility for OvertimeIQ
 * Uses sessionStorage for session-level caching and localStorage for persistent caching
 * Since database doesn't change often, we can cache aggressively
 */

const CACHE_PREFIX = 'overtimeiq_cache_';
const CACHE_VERSION = 'v1_';

// Cache TTL settings (in milliseconds)
export const CACHE_TTL = {
  SHORT: 5 * 60 * 1000,        // 5 minutes
  MEDIUM: 30 * 60 * 1000,      // 30 minutes
  LONG: 2 * 60 * 60 * 1000,    // 2 hours
  VERY_LONG: 24 * 60 * 60 * 1000  // 24 hours
};

/**
 * Generate a cache key from endpoint and params
 */
const generateCacheKey = (endpoint, params = {}) => {
  const paramStr = JSON.stringify(params, Object.keys(params).sort());
  const hash = btoa(paramStr).substring(0, 20);
  return `${CACHE_PREFIX}${CACHE_VERSION}${endpoint}_${hash}`;
};

/**
 * Get item from cache
 */
export const getCached = (endpoint, params = {}) => {
  const key = generateCacheKey(endpoint, params);
  
  // Try sessionStorage first (faster)
  let cached = sessionStorage.getItem(key);
  
  // Fall back to localStorage for persistent cache
  if (!cached) {
    cached = localStorage.getItem(key);
  }
  
  if (!cached) {
    return null;
  }
  
  try {
    const entry = JSON.parse(cached);
    
    // Check if expired
    if (Date.now() > entry.expiresAt) {
      // Remove expired entry
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
      return null;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Cache HIT: ${endpoint}`);
    }
    return entry.data;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Cache parse error:', e);
    }
    return null;
  }
};

/**
 * Set item in cache
 */
export const setCached = (endpoint, params = {}, data, ttl = CACHE_TTL.MEDIUM, persistent = false) => {
  const key = generateCacheKey(endpoint, params);
  
  const entry = {
    data,
    expiresAt: Date.now() + ttl,
    createdAt: Date.now()
  };
  
  try {
    const serialized = JSON.stringify(entry);
    
    // Always store in sessionStorage for fast access
    sessionStorage.setItem(key, serialized);
    
    // Also store in localStorage if persistent
    if (persistent) {
      localStorage.setItem(key, serialized);
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Cache SET: ${endpoint} (TTL: ${ttl / 1000}s)`);
    }
  } catch (e) {
    // Storage might be full, clear old entries
    if (process.env.NODE_ENV === 'development') {
      console.warn('Cache storage error, clearing old entries:', e);
    }
    clearOldCache();
  }
};

/**
 * Invalidate cache entries
 */
export const invalidateCache = (endpointPrefix = null) => {
  const keysToRemove = [];
  
  // Check sessionStorage
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      if (!endpointPrefix || key.includes(endpointPrefix)) {
        keysToRemove.push(key);
      }
    }
  }
  
  // Check localStorage
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      if (!endpointPrefix || key.includes(endpointPrefix)) {
        keysToRemove.push(key);
      }
    }
  }
  
  // Remove keys
  keysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
  
  if (process.env.NODE_ENV === 'development') {
    console.info(`Cache INVALIDATED: ${keysToRemove.length} entries`);
  }
  return keysToRemove.length;
};

/**
 * Clear old/expired cache entries
 */
export const clearOldCache = () => {
  const now = Date.now();
  let cleared = 0;
  
  // Check sessionStorage
  const sessionKeys = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    sessionKeys.push(sessionStorage.key(i));
  }
  
  sessionKeys.forEach(key => {
    if (key?.startsWith(CACHE_PREFIX)) {
      try {
        const entry = JSON.parse(sessionStorage.getItem(key));
        if (entry.expiresAt < now) {
          sessionStorage.removeItem(key);
          cleared++;
        }
      } catch (e) {
        sessionStorage.removeItem(key);
        cleared++;
      }
    }
  });
  
  // Check localStorage
  const localKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    localKeys.push(localStorage.key(i));
  }
  
  localKeys.forEach(key => {
    if (key?.startsWith(CACHE_PREFIX)) {
      try {
        const entry = JSON.parse(localStorage.getItem(key));
        if (entry.expiresAt < now) {
          localStorage.removeItem(key);
          cleared++;
        }
      } catch (e) {
        localStorage.removeItem(key);
        cleared++;
      }
    }
  });
  
  if (process.env.NODE_ENV === 'development') {
    console.info(`Cache CLEANUP: ${cleared} expired entries removed`);
  }
  return cleared;
};

/**
 * Cached fetch wrapper - fetches from cache first, then API
 */
export const cachedFetch = async (fetchFn, endpoint, params = {}, options = {}) => {
  const { ttl = CACHE_TTL.MEDIUM, persistent = false, forceRefresh = false } = options;
  
  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = getCached(endpoint, params);
    if (cached !== null) {
      return cached;
    }
  }
  
  // Fetch fresh data
  const data = await fetchFn();
  
  // Cache the result
  if (data) {
    setCached(endpoint, params, data, ttl, persistent);
  }
  
  return data;
};

/**
 * Cache statistics
 */
export const getCacheStats = () => {
  let sessionCount = 0;
  let localCount = 0;
  let totalSize = 0;
  
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      sessionCount++;
      totalSize += (sessionStorage.getItem(key)?.length || 0);
    }
  }
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      localCount++;
      totalSize += (localStorage.getItem(key)?.length || 0);
    }
  }
  
  return {
    sessionEntries: sessionCount,
    localEntries: localCount,
    totalEntries: sessionCount + localCount,
    estimatedSizeKB: Math.round(totalSize / 1024)
  };
};

export default {
  getCached,
  setCached,
  invalidateCache,
  clearOldCache,
  cachedFetch,
  getCacheStats,
  CACHE_TTL
};
